import Link from 'next/link';
import styled, { css } from 'styled-components';

import { IConstructorResult } from 'shared/types/ConstructorResult';
import { Image } from 'shared/components/Image';
import { variableFontRegular } from 'styles/theme/text';
import { toggleVisible } from 'styles/theme/animation';

interface ILoaderImageResultsGrid {
  align: 'left' | 'center' | 'right';
  product: IConstructorResult;
  imageSize?: string;
  disableLazyLoad?: boolean;
}

/**
 * [LoaderImageResultsGrid]
 * this result was created in order to reduce initial dom content load time, this returns a link grid element sans the image
 * Since the product wall won't carousel anymore, depending on the viewport, we may render more or
 * less images, so avoiding loading more images than needed on first load.
 */

export const LoaderImageResultsGrid = ({
  align,
  product: { data, value },
}: ILoaderImageResultsGrid) => (
  <LoaderImageResultsGrid.Wrapper data-qa="loader_image_grid_item">
    <Link
      href={`/${data?.category === 'shoes' ? 'sneakers' : 'apparel'}/${data.slug}`}
      passHref
      prefetch={false}
    >
      <LoaderImageResultsGrid.Link>
        <Image alt={value} data-qa="search_result_product_image" sizes="16px" src="/missing.png" />
      </LoaderImageResultsGrid.Link>
    </Link>
    <LoaderImageResultsGrid.Name align={align} data-qa="search_result_product_name">
      {value}
    </LoaderImageResultsGrid.Name>
  </LoaderImageResultsGrid.Wrapper>
);

LoaderImageResultsGrid.Wrapper = styled.div`
  position: relative;
  height: 100%;
`;

LoaderImageResultsGrid.Link = styled.a`
  display: block;
  height: 24px;
  max-width: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 24px;
`;

LoaderImageResultsGrid.Name = styled.div<{
  align?: ILoaderImageResultsGrid['align'];
}>`
  position: absolute;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  ${variableFontRegular({ size: '9.5px', height: '10px' })};
  visibility: hidden;
  opacity: 0;

  @media (hover: hover) and (pointer: fine) {
    a:hover ~ & {
      ${toggleVisible};
    }
  }

  ${({ align }) => {
    if (align === 'center') {
      return css`
        left: 50%;
        transform: translateX(-50%);
      `;
    }

    if (align === 'right') {
      return css`
        right: 0;
      `;
    }

    return css`
      left: 0;
    `;
  }};
`;
